import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Lock, Search, FolderOpen, Clock, AlertTriangle, Users, Send, Inbox, Reply, CheckCircle2, XCircle, ArrowRight, Mail, ListChecks, CalendarDays, Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const prioColors = {
  bassa: "bg-slate-100 text-slate-600", normale: "bg-blue-50 text-blue-700",
  alta: "bg-orange-100 text-orange-700", urgente: "bg-red-100 text-red-700",
};
const statusColors = {
  bozza: "bg-slate-100 text-slate-700", aperta: "bg-blue-100 text-blue-700",
  in_lavorazione: "bg-amber-100 text-amber-700", attesa_cliente: "bg-purple-100 text-purple-700",
  revisione: "bg-indigo-100 text-indigo-700", chiusa: "bg-emerald-100 text-emerald-700",
  archiviata: "bg-gray-200 text-gray-600",
};
const typeLabel = {
  informativa: "Informativa", richiesta_decisione: "Richiesta decisione",
  richiesta_integrazione: "Richiesta integrazione", sollecito: "Sollecito", delega: "Delega",
};

function KpiCard({ title, value, icon: Icon, accent, testid }) {
  return (
    <Card className="p-5 flex items-start gap-4 hover:border-navy-500 transition-colors" data-testid={testid}>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${accent}`}><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-3xl font-heading font-bold text-navy-900">{value}</div>
        <div className="text-xs text-slate-500 mt-1">{title}</div>
      </div>
    </Card>
  );
}

function MemoCardReceived({ m, onAction, onOpen }) {
  const unread = m.my_status === "non_letto";
  return (
    <div className={`group p-4 border rounded-md transition-all ${unread ? "border-amber-300 bg-amber-50/40" : "border-slate-200 bg-white"} hover:shadow-sm cursor-pointer`} onClick={() => onOpen(m)} data-testid={`memo-received-${m.id}`}>
      <div className="flex items-start gap-3">
        {unread && <div className="w-2 h-2 rounded-full bg-amber-500 mt-2 flex-shrink-0 animate-pulse" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-sm ${unread ? "font-semibold text-navy-900" : "text-slate-800"}`}>{m.subject}</span>
            <Badge className={`${prioColors[m.priority]} border-0 text-[10px]`}>{m.priority}</Badge>
            <Badge variant="outline" className="text-[10px]">{typeLabel[m.memo_type]}</Badge>
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
            <span>Da <strong className="text-slate-700">{m.sender_name}</strong></span>
            {m.project_protocol && <><span>·</span><span className="font-mono">{m.project_protocol}</span></>}
            <span>·</span><span>{new Date(m.created_at).toLocaleString("it-IT")}</span>
          </div>
          <div className={`text-sm mt-2 ${unread ? "blur-sm select-none text-slate-400" : "text-slate-700"} line-clamp-2`}>
            {unread ? "•••••• apri per leggere ••••••" : m.body}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-3 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAction(m, "respond")} data-testid={`memo-respond-${m.id}`}><Reply className="w-3 h-3 mr-1" />Rispondi</Button>
        <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-200 text-emerald-700" onClick={() => onAction(m, "accept")}><CheckCircle2 className="w-3 h-3 mr-1" />Accetta</Button>
        <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-600" onClick={() => onAction(m, "reject")}><XCircle className="w-3 h-3 mr-1" />Rifiuta</Button>
        {m.project_id && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAction(m, "convert_task")}>→ Task</Button>}
      </div>
    </div>
  );
}

function MemoCardSent({ m }) {
  const pending = m.recipients_meta.filter(r => r.status === "non_letto" || r.status === "letto");
  return (
    <div className="p-4 border border-slate-200 rounded-md bg-white" data-testid={`memo-sent-${m.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-medium text-navy-900">{m.subject}</span>
            <Badge className={`${prioColors[m.priority]} border-0 text-[10px]`}>{m.priority}</Badge>
            <Badge variant="outline" className="text-[10px]">{typeLabel[m.memo_type]}</Badge>
          </div>
          <div className="text-xs text-slate-500 mb-2">
            {m.project_protocol && <span className="font-mono mr-2">{m.project_protocol}</span>}
            <span>{new Date(m.created_at).toLocaleString("it-IT")}</span>
          </div>
          <div className="text-sm text-slate-700 line-clamp-2 mb-2">{m.body}</div>
        </div>
        {pending.length > 0 && <Badge className="bg-amber-100 text-amber-800 border-0">{pending.length} in attesa</Badge>}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {m.recipients_meta.map((r, i) => (
          <div key={i} className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
            <span>{r.user_name}</span>
            <span className={`w-1.5 h-1.5 rounded-full ${
              r.status === "non_letto" ? "bg-amber-500 animate-pulse" :
              r.status === "letto" ? "bg-blue-500" :
              r.status === "accettato" ? "bg-emerald-500" :
              r.status === "rifiutato" ? "bg-red-500" : "bg-slate-400"}`} />
            <span className="text-slate-500">{r.status.replace("_", " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ManagingPartnerDashboard({ data, onReload }) {
  const navigate = useNavigate();
  const [filterPrio, setFilterPrio] = useState("");
  const [filterType, setFilterType] = useState("");
  const [q, setQ] = useState("");

  const filterMemos = (list) => list.filter(m =>
    (!filterPrio || m.priority === filterPrio) &&
    (!filterType || m.memo_type === filterType) &&
    (!q || m.subject.toLowerCase().includes(q.toLowerCase()) || m.body.toLowerCase().includes(q.toLowerCase()) || (m.sender_name || "").toLowerCase().includes(q.toLowerCase()))
  );

  const handleOpen = async (m) => {
    if (m.my_status === "non_letto") {
      try { await api.post(`/memos/${m.id}/read`); onReload(); } catch {}
    }
    if (m.project_id) navigate(`/pratiche/${m.project_id}`);
  };

  const handleAction = async (m, action) => {
    try {
      await api.post(`/memos/${m.id}/action`, { action });
      toast.success(`Memo ${action === "respond" ? "risposto" : action === "accept" ? "accettato" : action === "reject" ? "rifiutato" : action === "convert_task" ? "convertito in task" : "archiviato"}`);
      onReload();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-mp">
      <div>
        <div className="text-xs uppercase tracking-widest text-amber-600">Vista Managing Partner</div>
        <h1 className="text-2xl font-heading font-bold text-navy-900 mt-1">Centro di controllo Studio</h1>
        <p className="text-sm text-slate-500 mt-1">Governo memo, deleghe, scadenze e attività dei collaboratori</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard title="Pratiche aperte" value={data.kpi.open_practices} icon={FolderOpen} accent="bg-navy-50 text-navy-700" testid="kpi-open" />
        <KpiCard title="Urgenti" value={data.kpi.urgent} icon={AlertTriangle} accent="bg-red-50 text-red-700" testid="kpi-urgent" />
        <KpiCard title="Scadenze 7gg" value={data.kpi.week_deadlines} icon={Clock} accent="bg-amber-50 text-amber-700" testid="kpi-week" />
        <KpiCard title="In ritardo" value={data.kpi.overdue} icon={AlertTriangle} accent="bg-orange-50 text-orange-700" testid="kpi-overdue" />
        <KpiCard title="Collaboratori" value={data.kpi.active_users} icon={Users} accent="bg-emerald-50 text-emerald-700" testid="kpi-users" />
      </div>

      <Card className="p-4">
        <div className="grid md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input className="pl-9" placeholder="Cerca memo per oggetto, mittente, testo..." value={q} onChange={(e)=>setQ(e.target.value)} data-testid="memo-search" />
          </div>
          <Select value={filterPrio || "_all"} onValueChange={(v)=>setFilterPrio(v === "_all" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Priorità"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tutte le priorità</SelectItem>
              <SelectItem value="normale">Normale</SelectItem><SelectItem value="alta">Alta</SelectItem><SelectItem value="urgente">Urgente</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterType || "_all"} onValueChange={(v)=>setFilterType(v === "_all" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Tipo"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tutti i tipi</SelectItem>
              {Object.entries(typeLabel).map(([k,l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5 flex flex-col" data-testid="memo-inbox-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 font-heading font-semibold text-navy-900">
              <Inbox className="w-4 h-4 text-amber-600" /> Memo ricevuti
              {data.memo_unread.length > 0 && <Badge className="bg-amber-500 text-white border-0 ml-2">{data.memo_unread.length} non letti</Badge>}
            </div>
            <Button variant="ghost" size="sm" onClick={()=>navigate("/memo")} className="text-xs text-navy-600" data-testid="open-memo-module">Vai al modulo →</Button>
          </div>
          <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
            {filterMemos(data.memo_pending).length === 0 ? (
              <div className="text-sm text-slate-400 text-center py-12">Nessun memo da processare</div>
            ) : filterMemos(data.memo_pending).map(m => (
              <MemoCardReceived key={m.id} m={m} onAction={handleAction} onOpen={handleOpen} />
            ))}
          </div>
        </Card>

        <Card className="p-5 flex flex-col" data-testid="memo-sent-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 font-heading font-semibold text-navy-900">
              <Send className="w-4 h-4 text-navy-600" /> Memo inviati
              {data.memo_sent_pending.length > 0 && <Badge variant="outline" className="ml-2">{data.memo_sent_pending.length} in attesa</Badge>}
            </div>
          </div>
          <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
            {filterMemos(data.memo_sent_pending).length === 0 ? (
              <div className="text-sm text-slate-400 text-center py-12">Nessun memo inviato in sospeso</div>
            ) : filterMemos(data.memo_sent_pending).map(m => (
              <MemoCardSent key={m.id} m={m} />
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="p-0 overflow-hidden lg:col-span-2">
          <div className="px-5 py-3 border-b border-slate-100 font-heading font-semibold text-navy-900">Pratiche dello Studio</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-2.5">Protocollo</th>
                <th className="text-left px-5 py-2.5">Titolo</th>
                <th className="text-left px-5 py-2.5">Stato</th>
                <th className="text-left px-5 py-2.5">Priorità</th>
                <th className="text-left px-5 py-2.5">Scadenza</th>
              </tr>
            </thead>
            <tbody>
              {data.my_practices.map(p => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={()=>navigate(`/pratiche/${p.id}`)}>
                  <td className="px-5 py-3 font-mono text-xs text-slate-600">{p.numero_protocollo}</td>
                  <td className="px-5 py-3 text-navy-900 font-medium">{p.title}</td>
                  <td className="px-5 py-3"><Badge className={`${statusColors[p.status]} border-0`}>{p.status.replace("_"," ")}</Badge></td>
                  <td className="px-5 py-3"><Badge className={`${prioColors[p.priority]} border-0`}>{p.priority}</Badge></td>
                  <td className="px-5 py-3 text-slate-600">{p.deadline || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 font-heading font-semibold text-navy-900 mb-3"><Activity className="w-4 h-4" /> Attività recente Studio</div>
          <div className="space-y-2.5 max-h-[300px] overflow-y-auto">
            {data.recent_activity.slice(0,10).map(a => (
              <div key={a.id} className="text-xs text-slate-600 flex gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-navy-500 mt-1.5 flex-shrink-0" />
                <div>
                  <span className="text-navy-900 font-medium">{a.user_name}</span> {a.action} <span className="italic text-slate-500">{a.entity_type}</span>
                  <div className="text-[10px] text-slate-400">{new Date(a.performed_at).toLocaleString("it-IT")}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

export function CollaboratorDashboard({ data, onReload }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleOpen = async (m) => {
    if (m.my_status === "non_letto") {
      try { await api.post(`/memos/${m.id}/read`); onReload(); } catch {}
    }
    if (m.project_id) navigate(`/pratiche/${m.project_id}`);
  };
  const handleAction = async (m, action) => {
    try { await api.post(`/memos/${m.id}/action`, { action }); toast.success("Aggiornato"); onReload(); }
    catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-collab">
      <div>
        <div className="text-xs uppercase tracking-widest text-amber-600">La mia scrivania</div>
        <h1 className="text-2xl font-heading font-bold text-navy-900 mt-1">Benvenuto, {user?.full_name?.split(" ").slice(-1)[0]}</h1>
        <p className="text-sm text-slate-500 mt-1">Memo, attività e progetti su cui sei coinvolto</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard title="Le mie pratiche" value={data.kpi.my_practices} icon={FolderOpen} accent="bg-navy-50 text-navy-700" />
        <KpiCard title="Task da svolgere" value={data.kpi.my_open_tasks} icon={ListChecks} accent="bg-emerald-50 text-emerald-700" />
        <KpiCard title="Task in ritardo" value={data.kpi.my_overdue_tasks} icon={AlertTriangle} accent="bg-red-50 text-red-700" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 font-heading font-semibold text-navy-900">
              <Inbox className="w-4 h-4 text-amber-600" /> Memo ricevuti
              {data.memo_unread.length > 0 && <Badge className="bg-amber-500 text-white border-0 ml-1">{data.memo_unread.length}</Badge>}
            </div>
            <Button variant="ghost" size="sm" onClick={()=>navigate("/memo")} className="text-xs">Tutti i memo →</Button>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
            {data.memo_pending.length === 0 ? (
              <div className="text-sm text-slate-400 text-center py-8">Nessun memo da processare</div>
            ) : data.memo_pending.map(m => (
              <MemoCardReceived key={m.id} m={m} onAction={handleAction} onOpen={handleOpen} />
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 font-heading font-semibold text-navy-900 mb-3"><ListChecks className="w-4 h-4 text-emerald-600" /> Le mie attività</div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {data.my_tasks.length === 0 ? (
              <div className="text-sm text-slate-400 text-center py-8">Nessuna attività assegnata</div>
            ) : data.my_tasks.map(t => (
              <button key={t.id} onClick={()=>navigate(`/pratiche/${t.project_id}`)} className="block w-full text-left p-2.5 border border-slate-100 rounded hover:bg-slate-50">
                <div className="text-sm text-navy-900 font-medium">{t.title}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <Badge className={`${prioColors[t.priority]} border-0 text-[10px]`}>{t.priority}</Badge>
                  {t.due_date && <span>scad. {t.due_date}</span>}
                  {t.project_protocol && <span className="font-mono text-slate-400">{t.project_protocol}</span>}
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="p-0 overflow-hidden lg:col-span-2">
          <div className="px-5 py-3 border-b border-slate-100 font-heading font-semibold text-navy-900">I miei progetti</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-5 py-2.5">Protocollo</th><th className="text-left px-5 py-2.5">Titolo</th><th className="text-left px-5 py-2.5">Stato</th><th className="text-left px-5 py-2.5">Scadenza</th></tr>
            </thead>
            <tbody>
              {data.my_practices.map(p => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={()=>navigate(`/pratiche/${p.id}`)}>
                  <td className="px-5 py-3 font-mono text-xs text-slate-600">{p.numero_protocollo}</td>
                  <td className="px-5 py-3 text-navy-900 font-medium">{p.title}</td>
                  <td className="px-5 py-3"><Badge className={`${statusColors[p.status]} border-0`}>{p.status.replace("_"," ")}</Badge></td>
                  <td className="px-5 py-3 text-slate-600">{p.deadline || "—"}</td>
                </tr>
              ))}
              {data.my_practices.length === 0 && <tr><td colSpan={4} className="text-center text-slate-400 py-8">Nessun progetto attivo</td></tr>}
            </tbody>
          </table>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 font-heading font-semibold text-navy-900 mb-3"><CalendarDays className="w-4 h-4 text-amber-600" /> Scadenze prossime</div>
          <div className="space-y-2.5">
            {data.upcoming.length === 0 && <div className="text-xs text-slate-400">Nessuna scadenza imminente</div>}
            {data.upcoming.map(p => (
              <button key={p.id} onClick={()=>navigate(`/pratiche/${p.id}`)} className="block w-full text-left text-sm">
                <div className="flex items-start gap-2">
                  <div className="w-1 self-stretch bg-amber-400 rounded-full" />
                  <div className="flex-1 min-w-0">
                    <div className="text-navy-900 font-medium truncate">{p.title}</div>
                    <div className="text-[11px] text-slate-500">{p.numero_protocollo} · {p.deadline}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const load = () => api.get("/dashboard").then(r => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-sm text-slate-500">Caricamento...</div>;
  return data.view === "managing_partner" ? <ManagingPartnerDashboard data={data} onReload={load} /> : <CollaboratorDashboard data={data} onReload={load} />;
}
